/**
 * The rows the admin schedule-override editor starts from on an NMI plan, and
 * the balance they reschedule.
 *
 * Mirrors the server planner (supabase/functions/_shared/nmi-admin-override.ts)
 * so the preview shows what will actually be written:
 *   - pending / retrying rows due within 24h stay put (the charge run may
 *     already hold them), which is the only race the planner guards;
 *   - past-due rows (declined / error / delinquent) join only when "include
 *     past-due" is on, and are never subject to that 24h rule. They are dated
 *     in the past by definition, so the rule used to drop every one of them
 *     and the checkbox did nothing (1OSUWO, 2026-09-28).
 *
 * Past-due money can't keep its past date, so it is folded into the balance
 * being rescheduled and spread over the future rows. With nothing left in the
 * future, one row a week out is offered as a starting point.
 */

export const OVERRIDE_IMMINENT_GRACE_MS = 24 * 3600 * 1000;
const PAST_DUE_STATUSES = new Set(['declined', 'error', 'delinquent']);
const CHARGEABLE_STATUSES = new Set(['pending', 'retrying']);

export function seedNmiOverrideRows(scheduleRows, { includePastDue = false, nowMs = Date.now() } = {}) {
  const future = [];
  let pastDueCents = 0;
  for (const r of scheduleRows || []) {
    const status = r?._nmi_status;
    const cents = Math.round(Number(r?.amount_cents) || 0);
    if (PAST_DUE_STATUSES.has(status)) {
      if (includePastDue) pastDueCents += cents;
      continue;
    }
    if (!CHARGEABLE_STATUSES.has(status)) continue;
    if (r?.date && new Date(r.date).getTime() <= nowMs + OVERRIDE_IMMINENT_GRACE_MS) continue;
    future.push({ due_date: String(r?.date || '').slice(0, 10), cents });
  }
  // Dates are the schedule: a retried row can leapfrog a later one's date,
  // which would trip the server's ascending-dates rule on data seeded here.
  future.sort((a, b) => (a.due_date < b.due_date ? -1 : a.due_date > b.due_date ? 1 : 0));

  let rows = future.map((r) => ({ due_date: r.due_date, amount: (r.cents / 100).toFixed(2) }));
  if (pastDueCents > 0 && rows.length === 0) {
    rows = [{
      due_date: new Date(nowMs + 7 * 86400000).toISOString().slice(0, 10),
      amount: (pastDueCents / 100).toFixed(2),
    }];
  }
  const baselineCents = future.reduce((s, r) => s + r.cents, 0) + pastDueCents;
  return { rows, baselineCents, pastDueCents };
}
